'use client';

import {
	ENUM_UTILS,
	MarketId,
	MarketKey,
	UISerializableOrder,
	OrderBookBidAsk,
	BidsAndAsks,
} from '@velocity-exchange/common';
import { BigNum, OrderType, PositionDirection } from '@velocity-exchange/sdk';
import { useCallback, useEffect, useState } from 'react';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { useCurrentAccountOrders } from './query/accountTradingData/useOrders';

const DEFAULT_USER_BIDS_AND_ASKS: BidsAndAsks = {
	bids: [],
	asks: [],
};

type AllMarketsUserBidsAndAsks = Record<MarketKey, BidsAndAsks>;

const useCurrentUserBidsAndAsks = (): {
	currentMarketBidsAndAsks: BidsAndAsks;
	getUserBidsAndAsksForMarket: (marketKey: MarketKey) => BidsAndAsks;
} => {
	const currentMarketId = useDriftStore(
		(s) => s.selectedMarket.current?.marketId
	);
	const allOrders = useCurrentAccountOrders();

	const [currentMarketBidsAndAsks, setCurrentMarketBidsAndAsks] =
		useState<BidsAndAsks>(DEFAULT_USER_BIDS_AND_ASKS);
	const [allMarketsUserBidsAndAsks, setAllMarketsUserBidsAndAsks] = useState<
		Record<MarketKey, BidsAndAsks>
	>({});

	const getOraclePrice = useGetOraclePriceForMarket();
	const [oraclePrice, setOraclePrice] = useState<BigNum | undefined>(
		getOraclePrice(currentMarketId)
	);

	useEffect(() => {
		// Oracle Price affects the price of oracle offset orders (because they are relative to the oracle price), so we need to trigger them to update when the oracle price updates - not only when the state of the orders changes. Make the oracle price update every second to avoid unnecessary computation when the oracle is ticking quickly.
		const interval = setInterval(() => {
			setOraclePrice(getOraclePrice(currentMarketId));
		}, 1000);
		return () => clearInterval(interval);
	}, [currentMarketId, getOraclePrice]);

	interface OrderBookBidAskWithMarket extends OrderBookBidAsk {
		marketKey: MarketKey;
	}

	const orderToBidAsk = (
		order: UISerializableOrder,
		oraclePrice: BigNum
	): OrderBookBidAskWithMarket => {
		const marketId = new MarketId(order.marketIndex, order.marketType);

		let price = order.price.toNum();

		// For limit orders with oracle offset, use oracle price + offset
		if (
			ENUM_UTILS.match(order.orderType, OrderType.LIMIT) &&
			order.oraclePriceOffset &&
			!order.oraclePriceOffset.eqZero() &&
			oraclePrice
		) {
			price = oraclePrice.add(order.oraclePriceOffset).toNum();
		}

		return {
			price,
			size: order.baseAssetAmount.sub(order.baseAssetAmountFilled).toNum(),
			type: 'dlob',
			marketKey: marketId.key,
		};
	};

	useEffect(() => {
		const allMarketsUserBidsAndAsks = allOrdersToUserBidsAndAsks(
			allOrders,
			oraclePrice as BigNum
		);
		setAllMarketsUserBidsAndAsks(allMarketsUserBidsAndAsks);
	}, [allOrders, oraclePrice]);

	useEffect(() => {
		if (allMarketsUserBidsAndAsks[currentMarketId?.key ?? ('0' as MarketKey)]) {
			setCurrentMarketBidsAndAsks(
				allMarketsUserBidsAndAsks[currentMarketId?.key ?? ('0' as MarketKey)]
			);
		} else {
			setCurrentMarketBidsAndAsks(DEFAULT_USER_BIDS_AND_ASKS);
		}
	}, [allMarketsUserBidsAndAsks, currentMarketId]);

	function allOrdersToUserBidsAndAsks(
		allOrders: UISerializableOrder[],
		oraclePrice: BigNum
	): AllMarketsUserBidsAndAsks {
		const bids = allOrders
			.filter(
				(order) =>
					ENUM_UTILS.match(order.direction, PositionDirection.LONG) &&
					!ENUM_UTILS.match(order.orderType, OrderType.MARKET)
			)
			.map((order) => orderToBidAsk(order, oraclePrice));

		const asks = allOrders
			.filter(
				(order) =>
					ENUM_UTILS.match(order.direction, PositionDirection.SHORT) &&
					!ENUM_UTILS.match(order.orderType, OrderType.MARKET)
			)
			.map((order) => orderToBidAsk(order, oraclePrice));

		const allMarketsWithUserOrders = Array.from(
			new Set(bids.map((b) => b.marketKey).concat(asks.map((a) => a.marketKey)))
		);

		const allMarketsUserBidsAndAsks: AllMarketsUserBidsAndAsks = {};

		allMarketsWithUserOrders.forEach((marketKey) => {
			const bidsForCurrentMarket = bids.filter(
				(b) => b.marketKey === marketKey
			);
			const asksForCurrentMarket = asks.filter(
				(a) => a.marketKey === marketKey
			);

			allMarketsUserBidsAndAsks[marketKey] = {
				bids: bidsForCurrentMarket,
				asks: asksForCurrentMarket,
			};
		});

		return allMarketsUserBidsAndAsks;
	}

	const getUserBidsAndAsksForMarket = useCallback(
		(marketKey: MarketKey) => {
			return allMarketsUserBidsAndAsks[marketKey] ?? DEFAULT_USER_BIDS_AND_ASKS;
		},
		[allMarketsUserBidsAndAsks]
	);

	return {
		currentMarketBidsAndAsks,
		getUserBidsAndAsksForMarket,
	};
};

export default useCurrentUserBidsAndAsks;
